import React, { useCallback, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import JGIcon from "Pages/Components/JGIcon";
import { FONT_SANS, GIFT_SLIDES, PURPLE } from "./checkoutV2.constants";

/**
 * Member-gift carousel for the redesign: cross-fades between the thank-you
 * gift images with a purple caption bar and arrow navigation. Advances only on
 * arrow click (no auto-advance, no looping); arrows disable at the ends.
 */
const MemberGiftSlider = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const count = GIFT_SLIDES.length;

  const goTo = useCallback(
    (next: number) => setActive(Math.max(0, Math.min(next, count - 1))),
    [count]
  );
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);
  const next = useCallback(() => goTo(active + 1), [active, goTo]);

  const isFirst = active === 0;
  const isLast = active === count - 1;

  return (
    // Card is 446×446; the image inside is 426×426 (a 10px purple frame).
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: {xs:394, md:446},
        margin: "0 auto",
        aspectRatio: "1 / 1",
        borderRadius: "12px",
        backgroundColor: PURPLE,
        p: "10px",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
        }}
      >
        {/* Clips only the photo to its rounded top corners — the caption below is
            a sibling, never a descendant of this clip, so it can't get cut off. */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            borderTopRightRadius: "12px",
            borderTopLeftRadius: "12px",
          }}
        >
        {GIFT_SLIDES.map((slide, index) => (
          <Box
            key={slide.image}
            component="img"
            src={slide.image}
            alt={t(slide.titleKey)}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: index === active ? 1 : 0,
              transition: "opacity .6s ease-in-out",
            }}
          />
        ))}
        </Box>
        {/* Arrow controls — translucent rounded squares centered on the edges. */}
        {[
          { side: "left", onClick: prev, icon: IoChevronBack, label: t("donationPage.previousGift"), disabled: isFirst },
          { side: "right", onClick: next, icon: IoChevronForward, label: t("donationPage.nextGift"), disabled: isLast },
        ].map(({ side, onClick, icon, label, disabled }) => (
          <ButtonBase
            key={side}
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            sx={{
              position: "absolute",
              top: "50%",
              [side]: -10,
              transform: "translateY(-50%)",
              width: 44,
              height: 44,
              borderRadius: "16px",
              color: "common.white",
              backgroundColor: "#7C6FE0",
              backdropFilter: "blur(2px)",
              transition: "background-color .2s ease",
            }}
          >
            <JGIcon
              is={icon}
              sx={{
                fontSize: 22,
                opacity: disabled ? 0.4 : 1,
                transition: "opacity .2s ease",
              }}
            />
          </ButtonBase>
        ))}

        {/* Caption overlaid on the bottom of the image. */}
        <Box
          sx={{
            position: "absolute",
            left: -1,
            right: -1,
            bottom: -10,
            pt: 1,
            pb:1.3,
            px: 2,
            textAlign: "center",
            backgroundColor: PURPLE,
          }}
        >
          <Typography
            sx={{
              fontFamily: "DM Sans",
              fontSize: {xs: "12px", md: "14px"},
              fontWeight: 500,
              color: "common.white",
              lineHeight: 1.3,
            }}
          >
            {t("donationPage.memberGift")}
          </Typography>
          <Typography
            key={active}
            sx={{
              fontFamily: FONT_SANS,
              fontSize: {xs: "20px", md: "24px"},
              fontWeight: 500,
              fontStyle: "italic",
              color: "common.white",
              lineHeight: 1.2,
              animation: "giftTitleFade .6s ease-in-out",
              "@keyframes giftTitleFade": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
          >
            {t(GIFT_SLIDES[active].titleKey)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MemberGiftSlider;
